<template>
  <div class="ig-metric-card" :class="is_detail_network ? 'border-box-analytics' : ''">

    <div class="ig-metric-header">
      <div class="d-flex align-items-center">
        <b-avatar :variant="`light-${color}`" size="36">
          <feather-icon size="16" :icon="icon" />
        </b-avatar>
        <span class="ig-metric-label ml-75">{{ label }}</span>
      </div>
      <b-button
        v-if="description"
        variant="flat-secondary"
        size="sm"
        class="btn-icon p-25"
        @click="showModal = true"
      >
        <feather-icon icon="InfoIcon" size="14" />
      </b-button>
    </div>

    <div class="ig-metric-value">{{ displayValue }}</div>

    <b-badge :variant="badgeVariant" class="ig-metric-badge px-75">
      {{ translatedTitle }}
    </b-badge>

    <b-progress :value="progressValue" max="100" height="4px" :variant="color" class="ig-metric-bar" />

    <div v-if="showAvg" class="ig-metric-avg">
      <feather-icon icon="BarChart2Icon" size="12" class="mr-25 text-muted" />
      <span class="text-muted">Promedio: {{ avgDisplay }}</span>
      <span :class="`ml-50 font-weight-bold text-${diffColor}`">{{ diffDisplay }}</span>
    </div>

    <p v-if="contextText" class="ig-metric-context text-muted">{{ contextText }}</p>

    <b-modal
      v-model="showModal"
      :title="label"
      size="sm"
      ok-only
      ok-title="Cerrar"
      ok-variant="outline-secondary"
      centered
      lazy
    >
      <div>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="modal-value">{{ displayValue }}</span>
          <b-badge :variant="badgeVariant" class="px-75">{{ translatedTitle }}</b-badge>
        </div>
        <b-progress :value="progressValue" max="100" height="6px" :variant="color" class="mb-1" />
        <p v-if="description" class="text-secondary small mb-1">{{ description }}</p>
        <div v-if="showAvg" class="p-1 rounded bg-light-secondary">
          <small class="text-muted d-block mb-25">Cuentas similares</small>
          <div class="d-flex align-items-center">
            <strong class="mr-75">{{ avgDisplay }}</strong>
            <b-badge :variant="diffColor">{{ diffDisplay }}</b-badge>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BProgress,
  BButton,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import { numberFormat } from '@/libs/utils/formats'

const LEVEL_PROGRESS = {
  'Excellent': 92,
  'Very Good': 80,
  'High': 80,
  'Organic': 72,
  'Good': 60,
  'Average': 42,
  'Fair': 30,
  'Poor': 20,
  'Low': 20,
}

const LEVEL_COLOR = {
  'Excellent': 'success',
  'Very Good': 'success',
  'High': 'success',
  'Organic': 'success',
  'Good': 'info',
  'Average': 'warning',
  'Fair': 'warning',
  'Poor': 'danger',
  'Low': 'danger',
}

export default {
  name: 'InstagramMetricCard',
  components: {
    BAvatar,
    BBadge,
    BProgress,
    BButton,
    BModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    metric: {
      type: Object,
      required: true,
      // Shape: { value: number, title: string, avg?: number }
    },
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: 'UsersIcon',
    },
    // When true, use metric.value directly as % (e.g. audience_authenticity 0-100)
    useRawProgress: {
      type: Boolean,
      default: false,
    },
    // When true, show only the translated title (no numeric value)
    qualitative: {
      type: Boolean,
      default: false,
    },
    // Optional humanized context line below the bar
    contextText: {
      type: String,
      default: '',
    },
    // Optional full description shown in the modal
    description: {
      type: String,
      default: '',
    },
    is_detail_network: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
    }
  },
  computed: {
    color() {
      const title = this.metric.title
      return LEVEL_COLOR[title] || 'primary'
    },
    badgeVariant() {
      const map = {
        success: 'light-success',
        info: 'light-info',
        warning: 'light-warning',
        danger: 'light-danger',
        primary: 'light-primary',
      }
      return map[this.color] || 'light-primary'
    },
    translatedTitle() {
      try {
        return this.$t(`instagram.${this.metric.title}`)
      } catch {
        return this.metric.title
      }
    },
    progressValue() {
      if (this.useRawProgress && this.metric.value) {
        return Math.min(Math.round(this.metric.value), 100)
      }
      return LEVEL_PROGRESS[this.metric.title] || 50
    },
    displayValue() {
      if (this.qualitative) return this.translatedTitle
      const val = this.metric.value || this.metric.avg || 0
      return `${numberFormat(val)}%`
    },
    showAvg() {
      return !this.qualitative && this.metric.avg !== null
    },
    avgDisplay() {
      return `${numberFormat(this.metric.avg)}%`
    },
    diff() {
      if (!this.metric.avg) return 0
      return (this.metric.value || 0) - this.metric.avg
    },
    diffDisplay() {
      const sign = this.diff >= 0 ? '+' : ''
      return `${sign}${numberFormat(this.diff)}%`
    },
    diffColor() {
      if (this.diff > 0) return 'success'
      if (this.diff < 0) return 'danger'
      return 'secondary'
    },
  },
}
</script>

<style scoped>
/* Contenedor principal — sin .card de Bootstrap para evitar display:flex */
.ig-metric-card {
  background: #fff;
  border-radius: 0.428rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  padding: 1rem 1.25rem;
}

/* Header: icono + label + botón info */
.ig-metric-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.ig-metric-label {
  font-size: 13px;
  color: #6e6b7b;
  line-height: 1.3;
}

/* Valor numérico grande */
.ig-metric-value {
  font-size: 22px;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: 0.5rem;
}

/* Badge de nivel */
.ig-metric-badge {
  font-size: 11px;
  display: inline-block;
  margin-bottom: 0.75rem;
}

/* Barra de progreso */
.ig-metric-bar {
  margin-bottom: 0.5rem;
}

/* Línea de comparación vs promedio */
.ig-metric-avg {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 0.5rem;
}

/* Texto de contexto */
.ig-metric-context {
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 0;
}

/* Modal */
.modal-value {
  font-size: 26px;
  font-weight: 500;
}
.bg-light-secondary {
  background-color: rgba(108, 117, 125, 0.08);
}
</style>
